import React, { useContext, useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TooltipItem,
  ChartTypeRegistry,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { DisplaySymbol } from '../../../utils/currency';
import { InputValue } from '../../../utils/data';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { FormattedMessage, useIntl } from 'react-intl';
import messages from 'src/apps/esg-ibm-flashsystems/locales/messages';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
);

interface Props {
  expectedAnnualCost: any;
  expectedAnnualCostIBM: any;
  pctTCO: number;
  inputValue: InputValue;
}

const AdministrativeBarChart: React.FC<Props> = ({
  expectedAnnualCost,
  expectedAnnualCostIBM,
  pctTCO,
  inputValue,
}: Props) => {
  const [legendPosition, setLegendPosition] = React.useState<'center' | 'end'>(
    'center',
  );
  ChartJS.defaults.borderColor = '#666666';
  ChartJS.defaults.color = '#000000';
  ChartJS.defaults.font.size = 14;
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < 768,
  );

  const intl = useIntl();
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1200) {
        setLegendPosition('center');
      } else {
        setLegendPosition('end');
      }
    };

    window.addEventListener('resize', handleResize);

    // Call the function to set the initial state
    handleResize();

    // Cleanup function for the effect
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth < 1024);
    }

    if (typeof window !== 'undefined') {
      handleResize();
    }

    window.addEventListener('resize', handleResize);
    return () => {
      // remove event listener when the component is unmounted to not cause any memory leaks
      // otherwise the event listener will continue to be active
      window.removeEventListener('resize', handleResize);
    };
    // add `isMobile` state variable as a dependency so that
    // it is called every time the window is resized
  }, [isMobile]);

  const { currency } = inputValue;
  const options = {
    maintainAspectRatio: false,
    plugins: {
      datalabels: {
        color: '#40BFFF',
        display: true,
        clip: true,
        formatter: function (value: string, ctx: any) {
          if (ctx.dataset.data[ctx.dataIndex] > 8000) {
            return ctx.chart.data.datasets[ctx.datasetIndex].label;
          } else {
            return '';
          }
        },
        font: {
          size: isMobile ? 8 : 10,
          style: 'normal' as const,
          family: "'IBM Plex Sans Arabic', sans-serif",
        },
        textAlign: 'center' as const,
      },
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context: TooltipItem<keyof ChartTypeRegistry>) {
            let label = context.dataset.label || '';

            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label +=
                DisplaySymbol(currency) + context.parsed.y.toLocaleString();
            }
            return label;
          },
        },
        TitleFont: {
          family: "'IBM Plex Sans Arabic', sans-serif",
        },
        bodyFont: {
          family: "'IBM Plex Sans Arabic', sans-serif",
        },
      },
    },
    responsive: true,
    options: {
      plugins: {
        datalabels: {
          color: 'white',
          font: {
            size: 40,
            family: "'IBM Plex Sans Arabic', sans-serif",
          },
          anchor: 'end',
          align: 'end',
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        font: {
          color: 'black',
          family: "'IBM Plex Sans Arabic', sans-serif",
        },
        grid: {
          display: false,
          drawTicks: false,
          drawBorder: false,
        },
        label: {
          display: false,
          font: {
            size: 14,
            family: "'IBM Plex Sans Arabic', sans-serif",
          },
        },
        ticks: {
          display: true,
          font: {
            size: 14,
            family: "'IBM Plex Sans Arabic', sans-serif",
          },
        },
      },
      y: {
        stacked: true,
        border: {
          color: 'black',
        },
        grid: {
          borderColor: '#666666',
          drawTicks: false,
          display: false,
        },
        title: {
          display: true,
          text: intl.formatMessage(messages.annual_administrative_savings),
          font: {
            size: 14,
            family: "'IBM Plex Sans Arabic', sans-serif",
          },
        },
        ticks: {
          display: false,
        },
      },
    },
  };

  const labels = [
    intl.formatMessage(messages.comparison),
    intl.formatMessage(messages.with_ibm),
  ];

  const costsSuppTicketsValues = [
    expectedAnnualCost.costsSuppTickets,
    expectedAnnualCostIBM.costsSuppTicketsIBM,
  ];
  const costsPlanningEvalValues = [
    expectedAnnualCost.costsPlanningEval,
    expectedAnnualCostIBM.costsPlanningEvalIBM,
  ];
  const costsMonitoringValues = [
    expectedAnnualCost.costsMonitoring,
    expectedAnnualCostIBM.costsMonitoringIBM,
  ];
  const costsAppOptimizationValues = [
    expectedAnnualCost.costsAppOptimization,
    expectedAnnualCostIBM.costsAppOptimizationIBM,
  ];
  const costsUserAccessValues = [
    expectedAnnualCost.costsUserAccess,
    expectedAnnualCostIBM.costsUserAccessIBM,
  ];
  const costsSecurityComplianceValues = [
    expectedAnnualCost.costsSecurityCompliance,
    expectedAnnualCostIBM.costsSecurityComplianceIBM,
  ];
  const costsDataProtectionValues = [
    expectedAnnualCost.costsDataProtection,
    expectedAnnualCostIBM.costsDataProtectionIBM,
  ];
  const costsNewInstallsValues = [
    expectedAnnualCost.costsNewInstalls,
    expectedAnnualCostIBM.costsNewInstallsIBM,
  ];

  const data = {
    labels,
    datasets: [
      {
        label: intl.formatMessage(messages.support_ticket),
        data: costsSuppTicketsValues.map((data) => data),
        backgroundColor: '#003a6d',
        datalabels: {
          color: 'white',
        },
      },
      {
        label: intl.formatMessage(messages.planning_evaluations),
        data: costsPlanningEvalValues.map((data) => data),
        backgroundColor: '#00539a',
        datalabels: {
          color: 'white',
        },
      },
      {
        label: intl.formatMessage(messages.monitoring_environment),
        data: costsMonitoringValues.map((data) => data),
        backgroundColor: '#0072c3',
        datalabels: {
          color: 'white',
        },
      },
      {
        label: intl.formatMessage(messages.application_opt),
        data: costsAppOptimizationValues.map((data) => data),
        backgroundColor: '#1192e8',
        datalabels: {
          color: 'white',
        },
      },
      {
        label: intl.formatMessage(messages.security_compliance),
        data: costsSecurityComplianceValues.map((data) => data),
        backgroundColor: '#33b1ff',
        datalabels: {
          color: 'white',
        },
      },
      {
        label: intl.formatMessage(messages.user_access),
        data: costsUserAccessValues.map((data) => data),
        backgroundColor: '#3ddbd9',
        datalabels: {
          color: 'white',
        },
      },
      {
        label: intl.formatMessage(messages.data_protection),
        data: costsDataProtectionValues.map((data) => data),
        backgroundColor: '#08bdba',
        datalabels: {
          color: 'white',
        },
      },
      {
        label: intl.formatMessage(messages.new_installs),
        data: costsNewInstallsValues.map((data) => data),
        backgroundColor: '#009d9a',
        datalabels: {
          color: 'white',
        },
      },
    ],
  };
  return (
    <div>
      <div className="standard-graph">
        <h2 className="graphText">
          <span className="num">{`${pctTCO}%`}</span>
          <br />
          <span className="text">
            <FormattedMessage {...messages.lower_storage} />
          </span>
        </h2>
        <Bar options={options} plugins={[ChartDataLabels]} data={data} />
      </div>
      <div className="graph-labels">
        <div className="graph-label">
          <div className="first-label-2"></div>
          <p>
            <FormattedMessage {...messages.new_installs} />
          </p>
        </div>
        <div className="graph-label">
          <div className="second-label-2"></div>
          <p>
            <FormattedMessage {...messages.data_protection} />
          </p>
        </div>
        <div className="graph-label">
          <div className="third-label-2"></div>
          <p>
            <FormattedMessage {...messages.user_access} />
          </p>
        </div>
        <div className="graph-label">
          <div className="fourth-label-2"></div>
          <p>
            <FormattedMessage {...messages.security_compliance} />
          </p>
        </div>
        <div className="graph-label">
          <div className="fifth-label-2"></div>
          <p>
            <FormattedMessage {...messages.application_opt} />
          </p>
        </div>
        <div className="graph-label">
          <div className="sixth-label-2"></div>
          <p>
            <FormattedMessage {...messages.monitoring_environment} />
          </p>
        </div>
        <div className="graph-label">
          <div className="seventh-label-2"></div>
          <p>
            <FormattedMessage {...messages.planning_evaluations} />
          </p>
        </div>
        <div className="graph-label">
          <div className="eighth-label-2"></div>
          <p>
            <FormattedMessage {...messages.support_ticket} />
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdministrativeBarChart;
